import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  channelLabel,
  type Channel,
  type ConnectWhatsAppInput,
  type CreateWhatsappTemplateInput,
  type TemplateButtonInput,
  type TokenStatus,
  type WebhookSubscription,
} from '@neuroedge/shared';
import { Button, Card, Modal, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useEmbeddedSignupPopup } from '../../lib/embedded-signup-popup';
import { CatalogStatusRow } from './CatalogStatus';
import { WebsiteChatSection } from './WebsiteChatSection';
import { useAuth } from '../../auth/AuthContext';
import {
  useChannels,
  useChannel,
  useConnectWhatsapp,
  useConnectWhatsappEmbeddedSignup,
  useReauthorizeWhatsapp,
  useDisconnectChannel,
  useSubscribeWhatsappWebhooks,
  useUpdateChannel,
  useUpdateWhatsappToken,
  useRefreshSendingHealth,
} from '../../api/channels';
import { NumberHealthSummary } from '../../components/whatsapp/NumberHealth';
import {
  useWhatsappTemplates,
  useSyncWhatsappTemplates,
  useCreateWhatsappTemplate,
} from '../../api/whatsapp-templates';

/**
 * The primary, guided WhatsApp connect path: launches Meta Embedded Signup
 * (a Meta-hosted popup), then posts its result to the server. No token ever
 * reaches this component - only the one-time `code`.
 */
function EmbeddedSignupButton() {
  const toast = useToast();
  const embeddedSignup = useConnectWhatsappEmbeddedSignup();
  const { launching, launch } = useEmbeddedSignupPopup((message) => toast.error(message));

  async function onClick() {
    await launch(async ({ code, wabaId, phoneNumberId }) => {
      try {
        await embeddedSignup.mutateAsync({ code, wabaId, phoneNumberId });
        toast.success('WhatsApp connected.');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not connect WhatsApp.');
      }
    });
  }

  return (
    <Button onClick={() => void onClick()} disabled={launching}>
      {launching ? 'Connecting…' : 'Connect WhatsApp (Guided)'}
    </Button>
  );
}

/**
 * Re-authorization: runs the same Embedded Signup popup against an
 * already-connected channel to obtain a fresh token, without disconnecting
 * it - shown once the current token is expiring soon or has expired.
 */
function ReauthorizeButton({ channelId }: { channelId: string }) {
  const toast = useToast();
  const reauthorize = useReauthorizeWhatsapp(channelId);
  const { launching, launch } = useEmbeddedSignupPopup((message) => toast.error(message));

  async function onClick() {
    await launch(async ({ code, wabaId, phoneNumberId }) => {
      try {
        await reauthorize.mutateAsync({ code, wabaId, phoneNumberId });
        toast.success('WhatsApp re-authorized.');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not re-authorize WhatsApp.');
      }
    });
  }

  return (
    <Button variant="secondary" onClick={() => void onClick()} disabled={launching}>
      {launching ? 'Reconnecting…' : 'Reconnect (Guided)'}
    </Button>
  );
}

const TOKEN_STATUS_LABEL: Record<TokenStatus, string> = {
  UNKNOWN: 'Active',
  OK: 'Active',
  EXPIRING_SOON: 'Expiring soon',
  EXPIRED: 'Expired',
};

const BLANK: ConnectWhatsAppInput = {
  externalBusinessAccountId: '',
  businessName: '',
  phoneNumberId: '',
  displayPhoneNumber: '',
  verifiedName: '',
  accessToken: '',
};

/** Mirrors the backend's `connectWhatsAppBody` limits (whatsapp.schemas.ts). */
function validate(form: ConnectWhatsAppInput): Record<string, string> {
  const errors: Record<string, string> = {};
  const businessName = form.businessName.trim();
  if (businessName.length < 1) errors.businessName = 'Enter a business name.';
  else if (businessName.length > 200) errors.businessName = 'Must be 200 characters or fewer.';

  const waba = form.externalBusinessAccountId.trim();
  if (waba.length < 1) errors.externalBusinessAccountId = 'Enter the WhatsApp Business Account ID.';
  else if (waba.length > 120) errors.externalBusinessAccountId = 'Must be 120 characters or fewer.';

  const phoneNumberId = form.phoneNumberId.trim();
  if (phoneNumberId.length < 1) errors.phoneNumberId = 'Enter the phone number ID.';
  else if (phoneNumberId.length > 120) errors.phoneNumberId = 'Must be 120 characters or fewer.';

  const displayPhoneNumber = form.displayPhoneNumber.trim();
  if (displayPhoneNumber.length < 1) errors.displayPhoneNumber = 'Enter the display phone number.';
  else if (displayPhoneNumber.length > 40)
    errors.displayPhoneNumber = 'Must be 40 characters or fewer.';

  const verifiedName = (form.verifiedName ?? '').trim();
  if (verifiedName.length > 200) errors.verifiedName = 'Must be 200 characters or fewer.';

  const accessToken = form.accessToken.trim();
  if (accessToken.length < 20) errors.accessToken = 'Access token must be at least 20 characters.';
  else if (accessToken.length > 4096)
    errors.accessToken = 'Access token must be 4096 characters or fewer.';

  return errors;
}

function ConnectWhatsAppForm() {
  const connect = useConnectWhatsapp();
  const toast = useToast();
  const [form, setForm] = useState<ConnectWhatsAppInput>(BLANK);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (k: keyof ConnectWhatsAppInput) => (e: { target: { value: string } }) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setFieldErrors((f) => {
      if (!f[k]) return f;
      const { [k]: _removed, ...rest } = f;
      return rest;
    });
  };

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    try {
      const created = await connect.mutateAsync({
        ...form,
        verifiedName: form.verifiedName?.trim() || undefined,
      });
      setForm(BLANK);
      setFieldErrors({});
      if (created.whatsapp?.account.webhookSubscription?.status === 'FAILED') {
        toast.error(
          'Channel connected, but incoming messages could not be set up - see “Receiving messages” on the channel.',
        );
      } else {
        toast.success('WhatsApp channel connected.');
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message = err instanceof Error ? err.message : 'Could not connect WhatsApp.';
        setError(message);
        toast.error(message);
      }
    }
  }

  const field = (
    key: keyof ConnectWhatsAppInput,
    label: string,
    opts: { placeholder?: string; type?: string; required?: boolean } = {},
  ) => (
    <TextField
      label={`${label}${opts.required === false ? '' : ' *'}`}
      type={opts.type ?? 'text'}
      placeholder={opts.placeholder}
      value={String(form[key] ?? '')}
      onChange={set(key)}
      error={fieldErrors[key]}
    />
  );

  return (
    <div>
      <p style={{ margin: '0 0 var(--space-3)', color: 'var(--color-text-muted)' }}>
        Values come from Meta Business Manager. Use a System User token with
        whatsapp_business_messaging, whatsapp_business_management and catalog_management. The access
        token is encrypted before it is stored and is never shown again. Use this only when Guided
        connect above is unavailable, or for a number already onboarded outside the app.
      </p>
      <form
        onSubmit={onSubmit}
        style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 480 }}
        noValidate
      >
        {field('businessName', 'Business name', { placeholder: 'Acme Ltd' })}
        {field('externalBusinessAccountId', 'WhatsApp Business Account ID', {
          placeholder: 'e.g. 1029384756',
        })}
        {field('phoneNumberId', 'Phone number ID', { placeholder: 'e.g. 5647382910' })}
        {field('displayPhoneNumber', 'Display phone number', { placeholder: '+1 555 010 0100' })}
        {field('verifiedName', 'Verified name', { required: false, placeholder: 'Acme Support' })}
        {field('accessToken', 'Access token', { type: 'password', placeholder: 'EAAG…' })}
        {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
        <div>
          <Button type="submit" disabled={connect.isPending}>
            Connect
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * Whether Meta is set up to deliver this number's inbound messages to us
 * (the platform app's webhook subscription on the WABA). A channel without
 * it looks connected and can send, but never receives anything - so this
 * says so plainly and offers the one-click fix.
 */
function WebhookSubscriptionStatus({
  channelId,
  subscription,
  canManage,
}: {
  channelId: string;
  subscription: WebhookSubscription | null;
  canManage: boolean;
}) {
  const subscribe = useSubscribeWhatsappWebhooks(channelId);
  const toast = useToast();

  async function onSubscribe() {
    try {
      await subscribe.mutateAsync();
      toast.success('Incoming messages are now set up for this number.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not set up incoming messages.');
    }
  }

  if (subscription?.status === 'SUBSCRIBED') {
    return (
      <span className="ne-badge" style={STATUS_BADGE_STYLE.CONNECTED}>
        Yes
      </span>
    );
  }

  const failed = subscription?.status === 'FAILED';
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', justifyItems: 'start' }}>
      <span style={{ color: failed ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>
        {failed
          ? `Not set up - Meta won’t deliver this number’s messages here. ${subscription.error ?? ''}`
          : 'Not verified - if messages to this number never arrive, set this up.'}
      </span>
      {canManage ? (
        <Button
          variant="secondary"
          disabled={subscribe.isPending}
          onClick={() => void onSubscribe()}
        >
          {subscribe.isPending ? 'Setting up…' : failed ? 'Retry' : 'Set up incoming messages'}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * In-app confirmation for Disconnect (not `window.confirm`, which a browser
 * can silently suppress - then nothing happens and nothing explains why).
 * Names the number, since several channels can share a business name, and
 * keeps any server error on screen instead of only a passing toast.
 */
function DisconnectChannelModal({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const disconnect = useDisconnectChannel();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  async function onConfirm() {
    setError(null);
    try {
      await disconnect.mutateAsync(channel.id);
      toast.success('Channel disconnected.');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not disconnect the channel.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal title="Disconnect channel" onClose={onClose}>
      <p style={{ marginTop: 0 }}>
        Disconnect <strong>{channelLabel(channel)}</strong>? It stops sending and receiving messages
        here. Conversations and message history are kept.
      </p>
      {error ? <p style={{ color: 'var(--color-danger)' }}>{error}</p> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <Button variant="secondary" onClick={onClose} disabled={disconnect.isPending}>
          Cancel
        </Button>
        <Button onClick={() => void onConfirm()} disabled={disconnect.isPending}>
          {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Paste a replacement token for this channel (e.g. a System User token after
 * a pasted user token was revoked). The server checks it against Meta before
 * it replaces the stored one, so a rejected token leaves the channel as it was.
 */
function UpdateTokenModal({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const update = useUpdateWhatsappToken(channel.id);
  const toast = useToast();
  const [token, setToken] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const value = token.trim();
    const clientError =
      value.length < 20
        ? 'Access token must be at least 20 characters.'
        : value.length > 4096
          ? 'Access token must be 4096 characters or fewer.'
          : undefined;
    setFieldError(clientError);
    if (clientError) return;

    try {
      const updated = await update.mutateAsync(value);
      toast.success(
        updated.whatsapp?.account.webhookSubscription?.status === 'FAILED'
          ? 'Token updated, but incoming messages still could not be set up - see “Receiving messages”.'
          : 'Access token updated.',
      );
      onClose();
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      const message = err instanceof Error ? err.message : 'Could not update the access token.';
      setFieldError(fields.accessToken ?? undefined);
      if (!fields.accessToken) setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal title="Update access token" onClose={onClose}>
      <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--space-3)' }} noValidate>
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          Replaces the token for <strong>{channelLabel(channel)}</strong>. Automations and
          conversations stay as they are. Use a System User token (expiry “Never”) with
          whatsapp_business_messaging, whatsapp_business_management and catalog_management (the last
          one lets us show your Meta Commerce catalog in chats). It is checked with Meta before it’s
          saved and is never shown again.
        </p>
        <TextField
          label="New access token *"
          type="password"
          placeholder="EAAG…"
          value={token}
          onChange={(e) => {
            setToken(e.target.value);
            setFieldError(undefined);
          }}
          error={fieldError}
        />
        {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button type="button" variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={update.isPending}>
            {update.isPending ? 'Checking with Meta…' : 'Update token'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Re-reads the number's messaging limit + quality from Meta. */
function RefreshHealthButton({ channelId }: { channelId: string }) {
  const refresh = useRefreshSendingHealth(channelId);
  const toast = useToast();
  return (
    <div>
      <Button
        variant="secondary"
        disabled={refresh.isPending}
        onClick={() =>
          refresh.mutate(undefined, {
            onSuccess: () => toast.success('Sending limit refreshed from Meta.'),
            onError: (err) => toast.error(err.message),
          })
        }
      >
        {refresh.isPending ? 'Refreshing…' : 'Refresh from Meta'}
      </Button>
    </div>
  );
}

function ChannelDetailCard({ id, canManage }: { id: string; canManage: boolean }) {
  const detail = useChannel(id);
  const [confirming, setConfirming] = useState(false);
  const [updatingToken, setUpdatingToken] = useState(false);

  if (detail.isPending) return null;
  if (detail.isError) return null;
  const c = detail.data;
  const tokenStatus = c.whatsapp?.account.tokenStatus;
  const tokenNeedsAttention = tokenStatus === 'EXPIRING_SOON' || tokenStatus === 'EXPIRED';

  return (
    <Card title={channelLabel(c)}>
      {canManage ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-2)',
          }}
        >
          {c.whatsapp ? (
            <>
              <ReauthorizeButton channelId={c.id} />
              <Button variant="secondary" onClick={() => setUpdatingToken(true)}>
                Update access token
              </Button>
            </>
          ) : null}
          <Button variant="secondary" onClick={() => setConfirming(true)}>
            Disconnect
          </Button>
        </div>
      ) : null}
      {confirming ? (
        <DisconnectChannelModal channel={c} onClose={() => setConfirming(false)} />
      ) : null}
      {updatingToken ? (
        <UpdateTokenModal channel={c} onClose={() => setUpdatingToken(false)} />
      ) : null}
      {tokenNeedsAttention ? (
        <p
          style={{
            margin: '0 0 var(--space-3)',
            color: tokenStatus === 'EXPIRED' ? 'var(--color-danger)' : 'var(--color-text-muted)',
          }}
        >
          {tokenStatus === 'EXPIRED'
            ? 'This channel’s connection has expired - messages may fail to send.'
            : 'This channel’s connection is expiring soon.'}{' '}
          {canManage
            ? 'Use Reconnect (Guided) or Update access token above to renew it without losing this number.'
            : null}
        </p>
      ) : null}
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: 'var(--space-2) var(--space-4)',
        }}
      >
        <dt style={{ color: 'var(--color-text-muted)' }}>Type</dt>
        <dd style={{ margin: 0 }}>{c.type}</dd>
        <dt style={{ color: 'var(--color-text-muted)' }}>Status</dt>
        <dd style={{ margin: 0 }}>
          <span className="ne-badge">{c.status}</span>
        </dd>
        {c.whatsapp ? (
          <>
            <dt style={{ color: 'var(--color-text-muted)' }}>Business account</dt>
            <dd style={{ margin: 0 }}>{c.whatsapp.account.externalBusinessAccountId}</dd>
            <dt style={{ color: 'var(--color-text-muted)' }}>Credential</dt>
            <dd style={{ margin: 0 }}>
              {c.whatsapp.account.hasCredential ? 'Stored (sealed)' : 'Missing'}
              {c.whatsapp.account.hasCredential ? (
                <>
                  {' '}
                  <span
                    className="ne-badge"
                    style={
                      tokenStatus === 'EXPIRED'
                        ? { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }
                        : undefined
                    }
                  >
                    {TOKEN_STATUS_LABEL[c.whatsapp.account.tokenStatus]}
                  </span>
                </>
              ) : null}
            </dd>
            <dt style={{ color: 'var(--color-text-muted)' }}>Receiving messages</dt>
            <dd style={{ margin: 0 }}>
              <WebhookSubscriptionStatus
                channelId={c.id}
                subscription={c.whatsapp.account.webhookSubscription}
                canManage={canManage}
              />
            </dd>
            <dt style={{ color: 'var(--color-text-muted)' }}>WhatsApp catalog</dt>
            <dd style={{ margin: 0 }}>
              <CatalogStatusRow channelId={c.id} canManage={canManage} />
            </dd>
            <dt style={{ color: 'var(--color-text-muted)' }}>Phone numbers</dt>
            <dd style={{ margin: 0 }}>
              {c.whatsapp.phoneNumbers.map((p) => (
                <div key={p.id}>
                  {p.displayPhoneNumber}
                  {p.verifiedName ? ` · ${p.verifiedName}` : ''}{' '}
                  <span className="ne-badge">{p.status}</span>
                </div>
              ))}
            </dd>
            <dt style={{ color: 'var(--color-text-muted)' }}>Sending limit</dt>
            <dd style={{ margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
              {c.whatsapp.phoneNumbers.map((p) => (
                <NumberHealthSummary
                  key={p.id}
                  health={
                    p.health ?? { qualityRating: null, messagingLimitTier: null, checkedAt: null }
                  }
                />
              ))}
              {canManage ? <RefreshHealthButton channelId={c.id} /> : null}
            </dd>
          </>
        ) : null}
      </dl>
    </Card>
  );
}

const TEMPLATE_STATUS_ORDER = ['APPROVED', 'PENDING', 'PAUSED', 'REJECTED', 'DISABLED'];

interface TemplateButtonRow {
  type: TemplateButtonInput['type'];
  text: string;
  url: string;
  urlExample: string;
  phoneNumber: string;
}

const BLANK_BUTTON_ROW: TemplateButtonRow = {
  type: 'QUICK_REPLY',
  text: '',
  url: '',
  urlExample: '',
  phoneNumber: '',
};

/** Highest `{{n}}` in the body - one sample value is needed for each. */
function templateVariableCount(bodyText: string): number {
  let max = 0;
  for (const m of bodyText.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) max = Math.max(max, Number(m[1]));
  return Math.min(max, 50);
}

const urlHasVariable = (url: string) => /\{\{\s*1\s*\}\}/.test(url);

function validateTemplate(
  name: string,
  language: string,
  bodyText: string,
  samples: string[],
  buttons: TemplateButtonRow[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!name.trim()) errors.name = 'Enter a template name.';
  else if (!/^[a-z0-9_]+$/.test(name.trim())) {
    errors.name = 'Use lowercase letters, numbers, and underscores only.';
  }
  if (!language.trim()) errors.language = 'Enter a language code, e.g. en_US.';
  const body = bodyText.trim();
  if (!body) errors.bodyText = 'Enter the message body.';
  else if (/^\{\{\s*\d+\s*\}\}/.test(body) || /\{\{\s*\d+\s*\}\}[.!?]?$/.test(body)) {
    errors.bodyText = 'The body cannot start or end with a variable - add some words around it.';
  } else if (/\}\}\s*\{\{/.test(body)) {
    errors.bodyText = 'Two variables cannot be side by side - put words between them.';
  }
  for (let i = 0; i < templateVariableCount(bodyText); i += 1) {
    if (!samples[i]?.trim()) errors[`bodyExamples/${i}`] = `Enter a sample value for {{${i + 1}}}.`;
  }
  buttons.forEach((b, i) => {
    if (!b.text.trim()) errors[`buttons/${i}/text`] = 'Enter the button text.';
    if (b.type === 'URL' && !b.url.trim()) errors[`buttons/${i}/url`] = 'Enter the link.';
    if (b.type === 'URL' && urlHasVariable(b.url) && !b.urlExample.trim()) {
      errors[`buttons/${i}/example`] = 'Enter a full sample link for the {{1}}.';
    }
    if (b.type === 'PHONE_NUMBER' && !b.phoneNumber.trim()) {
      errors[`buttons/${i}/phoneNumber`] = 'Enter the phone number.';
    }
  });
  return errors;
}

function toButtonInput(row: TemplateButtonRow): TemplateButtonInput {
  if (row.type === 'URL') {
    return {
      type: 'URL',
      text: row.text,
      url: row.url,
      ...(urlHasVariable(row.url) && row.urlExample.trim()
        ? { example: row.urlExample.trim() }
        : {}),
    };
  }
  if (row.type === 'PHONE_NUMBER') {
    return { type: 'PHONE_NUMBER', text: row.text, phoneNumber: row.phoneNumber };
  }
  return { type: 'QUICK_REPLY', text: row.text };
}

/**
 * Self-service template authoring: submits a new template straight to Meta
 * for review (`POST /whatsapp/templates`) using the channel's already-sealed
 * token. Approval still happens on Meta's side - it lands here as `PENDING`
 * and "Sync from Meta" above will pick up the eventual approved/rejected
 * status, same as any other template.
 */
function CreateTemplateForm({ channelId }: { channelId: string }) {
  const create = useCreateWhatsappTemplate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [category, setCategory] = useState<CreateWhatsappTemplateInput['category']>('UTILITY');
  const [bodyText, setBodyText] = useState('');
  const [footerText, setFooterText] = useState('');
  const [samples, setSamples] = useState<string[]>([]);
  const [buttons, setButtons] = useState<TemplateButtonRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const variableCount = templateVariableCount(bodyText);

  function updateButton(i: number, patch: Partial<TemplateButtonRow>) {
    setButtons((rows) => rows.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const errors = validateTemplate(name, language, bodyText, samples, buttons);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    try {
      await create.mutateAsync({
        channelId,
        name: name.trim(),
        language: language.trim(),
        category,
        bodyText: bodyText.trim(),
        bodyExamples:
          variableCount > 0 ? samples.slice(0, variableCount).map((s) => s.trim()) : undefined,
        footerText: footerText.trim() || undefined,
        buttons: buttons.length > 0 ? buttons.map(toButtonInput) : undefined,
      });
      setName('');
      setBodyText('');
      setSamples([]);
      setFooterText('');
      setButtons([]);
      toast.success('Template submitted for Meta review.');
    } catch (err) {
      let message: string;
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          message = 'Please fix the highlighted fields.';
        } else {
          message = err.message;
        }
      } else {
        message = err instanceof Error ? err.message : 'Could not create the template.';
      }
      setError(message);
      toast.error(message);
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--space-3)' }} noValidate>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Submitted to Meta for review - starts PENDING until Meta approves it.
      </p>
      <TextField
        label="Template name *"
        placeholder="order_update"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={fieldErrors.name}
      />
      <TextField
        label="Language *"
        placeholder="en_US"
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
        error={fieldErrors.language}
      />
      <div className="ne-field">
        <label className="ne-field__label" htmlFor="template-category">
          Category *
        </label>
        <select
          id="template-category"
          className="ne-input"
          value={category}
          onChange={(e) => setCategory(e.target.value as CreateWhatsappTemplateInput['category'])}
        >
          <option value="UTILITY">Utility</option>
          <option value="MARKETING">Marketing</option>
          <option value="AUTHENTICATION">Authentication</option>
        </select>
      </div>
      <div className="ne-field">
        <label className="ne-field__label" htmlFor="template-body">
          Body text *
        </label>
        <textarea
          id="template-body"
          className="ne-input"
          rows={3}
          placeholder="Hi {{1}}, your order {{2}} has shipped."
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
        />
        {fieldErrors.bodyText ? (
          <span className="ne-field__error">{fieldErrors.bodyText}</span>
        ) : null}
      </div>
      {variableCount > 0 ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <span style={{ color: 'var(--color-text-muted)' }}>
            Sample values - Meta reviews the template with these filled in.
          </span>
          {Array.from({ length: variableCount }, (_, i) => (
            <TextField
              key={i}
              label={`Sample for {{${i + 1}}} *`}
              placeholder={i === 0 ? 'Ravi' : 'e.g. ORD-1042'}
              value={samples[i] ?? ''}
              onChange={(e) =>
                setSamples((rows) => {
                  const next = [...rows];
                  next[i] = e.target.value;
                  return next;
                })
              }
              error={fieldErrors[`bodyExamples/${i}`]}
            />
          ))}
          {fieldErrors.bodyExamples ? (
            <span className="ne-field__error">{fieldErrors.bodyExamples}</span>
          ) : null}
        </div>
      ) : null}
      <TextField
        label="Footer (optional)"
        placeholder="Thanks for shopping with us"
        value={footerText}
        onChange={(e) => setFooterText(e.target.value)}
        error={fieldErrors.footerText}
      />

      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-2)',
          }}
        >
          <span style={{ color: 'var(--color-text-muted)' }}>Buttons (up to 3, optional)</span>
          {buttons.length < 3 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setButtons((rows) => [...rows, { ...BLANK_BUTTON_ROW }])}
            >
              Add button
            </Button>
          ) : null}
        </div>
        {buttons.map((b, i) => (
          <div key={i}>
            <div
              className="ne-row-editor"
              style={{ marginBottom: 'var(--space-2)', alignItems: 'center' }}
            >
              <select
                className="ne-input"
                style={{ maxWidth: 160 }}
                aria-label="Button type"
                value={b.type}
                onChange={(e) =>
                  updateButton(i, { type: e.target.value as TemplateButtonRow['type'] })
                }
              >
                <option value="QUICK_REPLY">Quick reply</option>
                <option value="URL">Website URL</option>
                <option value="PHONE_NUMBER">Phone number</option>
              </select>
              <input
                className="ne-input"
                aria-label="Button text"
                placeholder="Button text"
                value={b.text}
                onChange={(e) => updateButton(i, { text: e.target.value })}
              />
              {b.type === 'URL' ? (
                <input
                  className="ne-input"
                  aria-label="Button URL"
                  placeholder="https://example.com"
                  value={b.url}
                  onChange={(e) => updateButton(i, { url: e.target.value })}
                />
              ) : null}
              {b.type === 'URL' && urlHasVariable(b.url) ? (
                <input
                  className="ne-input"
                  aria-label="Sample link"
                  placeholder="https://example.com/orders/1042"
                  value={b.urlExample}
                  onChange={(e) => updateButton(i, { urlExample: e.target.value })}
                />
              ) : null}
              {b.type === 'PHONE_NUMBER' ? (
                <input
                  className="ne-input"
                  aria-label="Button phone number"
                  placeholder="+1 555 010 0100"
                  value={b.phoneNumber}
                  onChange={(e) => updateButton(i, { phoneNumber: e.target.value })}
                />
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={() => setButtons((rows) => rows.filter((_, idx) => idx !== i))}
              >
                Remove
              </Button>
            </div>
            {Object.entries(fieldErrors)
              .filter(([k]) => k.startsWith(`buttons/${i}/`))
              .map(([k, msg]) => (
                <span key={k} className="ne-field__error" style={{ display: 'block' }}>
                  {msg}
                </span>
              ))}
          </div>
        ))}
      </div>

      {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
      <div>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Submitting…' : 'Submit for review'}
        </Button>
      </div>
    </form>
  );
}

function ChannelTemplates({ channelId, canManage }: { channelId: string; canManage: boolean }) {
  const templates = useWhatsappTemplates({ channelId, pageSize: 100 });
  const sync = useSyncWhatsappTemplates();
  const channel = useChannel(channelId);
  const update = useUpdateChannel(channelId);
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  const rows = [...(templates.data?.items ?? [])].sort(
    (a, b) =>
      TEMPLATE_STATUS_ORDER.indexOf(a.status) - TEMPLATE_STATUS_ORDER.indexOf(b.status) ||
      a.name.localeCompare(b.name),
  );

  const autoReplyOptions = rows.filter((t) => t.status === 'APPROVED' && t.bodyVariableCount === 0);
  const currentAutoReply = channel.data?.autoReplyTemplateName ?? '';

  async function onSync() {
    setError(null);
    try {
      await sync.mutateAsync({ channelId });
      toast.success('Templates synced.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not sync templates.';
      setError(message);
      toast.error(message);
    }
  }

  async function onAutoReplyChange(name: string) {
    setError(null);
    try {
      await update.mutateAsync({ autoReplyTemplateName: name || null });
      toast.success('Auto-reply updated.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not update the auto-reply.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Card title="Message templates">
      {canManage ? (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-2)' }}
        >
          <Button variant="secondary" disabled={sync.isPending} onClick={onSync}>
            {sync.isPending ? 'Syncing…' : 'Sync from Meta'}
          </Button>
        </div>
      ) : null}

      {canManage ? (
        <details style={{ marginBottom: 'var(--space-3)' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--color-text-muted)' }}>
            Create a new template
          </summary>
          <div style={{ marginTop: 'var(--space-3)' }}>
            <CreateTemplateForm channelId={channelId} />
          </div>
        </details>
      ) : null}

      {canManage ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-3)',
            flexWrap: 'wrap',
          }}
        >
          <label htmlFor={`auto-${channelId}`} style={{ color: 'var(--color-text-muted)' }}>
            Auto-reply on first message
          </label>
          <select
            id={`auto-${channelId}`}
            className="ne-input"
            style={{ width: 'auto' }}
            aria-label="Auto-reply template"
            value={currentAutoReply}
            disabled={update.isPending}
            onChange={(e) => void onAutoReplyChange(e.target.value)}
          >
            <option value="">Off</option>
            {autoReplyOptions.map((t) => (
              <option key={t.id} value={t.name}>
                {t.name}
              </option>
            ))}
            {currentAutoReply && !autoReplyOptions.some((t) => t.name === currentAutoReply) ? (
              <option value={currentAutoReply}>{currentAutoReply}</option>
            ) : null}
          </select>
        </div>
      ) : null}

      {error ? <p style={{ margin: 0, color: 'var(--color-danger)' }}>{error}</p> : null}
      {templates.isPending ? (
        <PageState kind="loading" message="Loading templates…" />
      ) : rows.length === 0 ? (
        <PageState
          kind="empty"
          title="No templates"
          message={canManage ? 'Sync from Meta to import approved templates.' : undefined}
        />
      ) : (
        <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
          {rows.map((t) => (
            <li key={t.id}>
              {t.name} · {t.language} <span className="ne-badge">{t.status}</span>
              {t.bodyVariableCount > 0 ? ` · ${t.bodyVariableCount} var` : ''}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

const STATUS_BADGE_STYLE: Partial<
  Record<Channel['status'], { borderColor: string; color: string }>
> = {
  CONNECTED: { borderColor: 'var(--color-success)', color: 'var(--color-success)' },
  ERROR: { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' },
  DISCONNECTED: { borderColor: 'var(--color-danger)', color: 'var(--color-danger)' },
};

/**
 * One channel's business-card + templates, collapsed behind a single
 * `<details>` header - with several connected channels, showing every card
 * expanded at once made the page very long. Defaults open only when it's the
 * sole channel; otherwise collapsed so the page opens to a scannable list of
 * headers.
 */
function ChannelGroup({
  channel,
  canManage,
  defaultOpen,
}: {
  channel: Channel;
  canManage: boolean;
  defaultOpen: boolean;
}) {
  return (
    <details className="ne-channel-group" open={defaultOpen}>
      <summary className="ne-channel-group__summary">
        <span className="ne-channel-group__name">{channelLabel(channel)}</span>
        <span className="ne-badge" style={STATUS_BADGE_STYLE[channel.status]}>
          {channel.status}
        </span>
      </summary>
      <div className="ne-channel-group__body">
        <ChannelDetailCard id={channel.id} canManage={canManage} />
        {channel.type === 'WHATSAPP' ? (
          <ChannelTemplates channelId={channel.id} canManage={canManage} />
        ) : null}
      </div>
    </details>
  );
}

/**
 * Disconnected channels are soft-deleted (`GET /channels/:id` 404s for
 * them), so this only shows the summary fields the list already returns -
 * no business-card/template drill-down, just enough to see what was
 * connected and when it was disconnected. Reconnecting the same WhatsApp
 * Business Account/number is just "Connect WhatsApp" below - the ids free up
 * as soon as a channel is disconnected.
 */
function DisconnectedChannels({ channels }: { channels: Channel[] }) {
  if (channels.length === 0) return null;
  return (
    <details className="ne-channel-group">
      <summary className="ne-channel-group__summary">
        <span className="ne-channel-group__name">Disconnected channels ({channels.length})</span>
      </summary>
      <div className="ne-channel-group__body">
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-text-muted)' }}>
          Conversations and message history are kept. To reconnect the same WhatsApp Business
          Account or number, use Connect WhatsApp below.
        </p>
        <ul style={{ margin: 0, paddingLeft: 'var(--space-4)' }}>
          {channels.map((c) => (
            <li key={c.id}>
              {channelLabel(c)} <span className="ne-badge">{c.type}</span>{' '}
              <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                disconnected {new Date(c.updatedAt).toLocaleDateString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

export function ChannelsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('channels.manage');
  const channels = useChannels({ includeDisconnected: true });

  // Website chats have their own section below.
  const active =
    channels.data?.filter((c) => c.status !== 'DISCONNECTED' && c.type !== 'WEBSITE') ?? [];
  const disconnected = channels.data?.filter((c) => c.status === 'DISCONNECTED') ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 760 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Channels</h1>

      {channels.isPending ? (
        <PageState kind="loading" message="Loading channels…" />
      ) : channels.isError ? (
        <PageState
          kind="error"
          message={channels.error.message}
          onRetry={() => void channels.refetch()}
        />
      ) : active.length === 0 && disconnected.length === 0 ? (
        <PageState
          kind="empty"
          title="No channels connected"
          message="Connect a WhatsApp Business Account below."
        />
      ) : (
        <>
          {active.map((c) => (
            <ChannelGroup
              key={c.id}
              channel={c}
              canManage={canManage}
              defaultOpen={active.length === 1}
            />
          ))}
          <DisconnectedChannels channels={disconnected} />
        </>
      )}

      {canManage ? (
        <Card title="Connect WhatsApp">
          <p style={{ margin: '0 0 var(--space-3)', color: 'var(--color-text-muted)' }}>
            Connect your own WhatsApp Business Account in a few clicks — a Meta-hosted popup walks
            you through authorizing your WABA and phone number. No admin needed.
          </p>
          <EmbeddedSignupButton />
          <details style={{ marginTop: 'var(--space-4)' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--color-text-muted)' }}>
              Connect manually (advanced)
            </summary>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <ConnectWhatsAppForm />
            </div>
          </details>
        </Card>
      ) : null}

      <WebsiteChatSection />

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
